/**
 * A controlled tooltip component that works with both Link and button elements.
 * When href is provided, it renders as a Next.js Link.
 * When href is not provided, it renders as a button.
 */
import Link from "next/link";
import { Tooltip } from "@zapier/design-system-beta";
import { ReactNode } from "react";

type BaseProps = {
  key?: string;
  content: string;
  ariaLabel?: string;
  placement?: "top" | "bottom" | "left" | "right";
  offset?: "small" | "medium" | "large";
  className?: string;
  children: ReactNode;
};

type LinkProps = BaseProps & {
  href: string;
  target: string;
  onClick?: never;
};

type ButtonProps = BaseProps & {
  href?: never;
  target?: never;
  onClick: () => void;
};

type Props = LinkProps | ButtonProps;

export function ButtonOrLinkWithTooltip({
  key,
  content,
  ariaLabel,
  placement = "top",
  offset = "large",
  className,
  onClick,
  href,
  target,
  children,
}: Props) {
  const getTag = (href: Props["href"]) => {
    if (href) {
      return Link;
    }
    return "button";
  };

  const Component = getTag(href) as typeof Link | "button";

  return (
    <Tooltip key={key} content={content} placement={placement} offset={offset}>
      <Tooltip.FocusableTrigger>
        {/* @ts-ignore - Component is either Link or button based on href prop */}
        <Component
          aria-label={ariaLabel}
          className={className}
          onClick={onClick}
          {...(href ? { href, target } : {})}
        >
          {children}
        </Component>
      </Tooltip.FocusableTrigger>
    </Tooltip>
  );
}
